import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer as observableDefer, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { CancelRefundDetailsRequest } from '../../dtos/request/cancel-refund/cancel-refund-details-request';
import { ProcessCancelRefundRequest } from '../../dtos/request/cancel-refund/process-cancel-refund-request';
import { CancelRefundDetailsResponse } from '../../dtos/response/cancel-refund/cancel-refund-details-response';
import { ProcessCancelRefundResponse } from '../../dtos/response/cancel-refund/process-cancel-refund-response';

export interface CancelRefundServiceAPI {
  /**
   * Gets cancel refund details for a given confirmation code
   */
  getCancelRefundDetails(request: CancelRefundDetailsRequest): Observable<CancelRefundDetailsResponse>;

  /**
   * Processes the cancel refund
   */
  processCancelRefund(request: ProcessCancelRefundRequest): Observable<ProcessCancelRefundResponse>;
}

@Injectable({
  providedIn: 'root',
})
export class CancelRefundService implements CancelRefundServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  getCancelRefundDetails(request: CancelRefundDetailsRequest): Observable<CancelRefundDetailsResponse> {
    const options = {
      headers: new HttpHeaders({ background: 'false' }),
    };
    return this.http.post<CancelRefundDetailsResponse>(`api/cancel-refund/details`, request, options).pipe(
      timeout({
        each: TimeoutLimit.LONG,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => response),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
        }
        return observableThrowError(() => err);
      })
    );
  }

  processCancelRefund(request: ProcessCancelRefundRequest): Observable<ProcessCancelRefundResponse> {
    const options = {
      headers: new HttpHeaders({ background: 'false' }),
    };
    return this.http.put<ProcessCancelRefundResponse>(`api/cancel-refund/process`, request, options).pipe(
      timeout({
        each: TimeoutLimit.LONG,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => response),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
        }
        return observableThrowError(() => err);
      })
    );
  }
}
